import { useTranslation } from "react-i18next";
import { CAlert, CBadge, CCol, CRow, CSpinner } from "@coreui/react-pro";
import {
  type PartnerOfferDecision,
  type PartnerOfferDto,
  type PartnerOfferStatus,
  usePartnerOffers,
  useRecordPartnerOfferDecision,
} from "../../api/hooks/usePartnerOffers";
import { useToast } from "../../context/useToast";
import { PartnerOfferCard } from "../../components/common/PartnerOfferCard";

/** Colour carries the same meaning as the back office panel: green converted, grey never needed. */
const STATUS_COLOUR: Partial<Record<PartnerOfferStatus, string>> = {
  SignedUpViaScryer: "success",
  AlreadyUsing: "secondary",
  ClickedThrough: "info",
  Snoozed: "warning",
};

const STATUS_KEY: Partial<Record<PartnerOfferStatus, string>> = {
  SignedUpViaScryer: "signedUpViaScryer",
  AlreadyUsing: "alreadyUsing",
  ClickedThrough: "clickedThrough",
  Snoozed: "snoozed",
};

/**
 * Every partner, whatever the customer has said about them.
 *
 * The dashboard slot is a prompt; this is the home. Without it a dismissed offer would be
 * unreachable, which is a poor deal for someone who clicked the wrong thing once — so this page is
 * also where "I already use this" gets undone.
 */
export function PartnerOffers() {
  const { t } = useTranslation("partnerOffers");
  const { showSuccess } = useToast();
  const offers = usePartnerOffers();
  const record = useRecordPartnerOfferDecision();

  const handleDecision = (partnerKey: string, decision: PartnerOfferDecision) => {
    record.mutate(
      { partnerKey, decision },
      {
        onSuccess: () => {
          if (decision === "Reopen") showSuccess(t("toast.reopened"));
          else if (decision === "AlreadyUsing") showSuccess(t("toast.alreadyUsing"));
          else if (decision === "ConfirmSignedUp") showSuccess(t("toast.signedUp"));
        },
      }
    );
  };

  const partners = offers.data?.partners ?? [];

  return (
    <div>
      <h1 className="mb-1">{t("page.title")}</h1>
      <p className="text-body-secondary mb-4" style={{ maxWidth: "70ch" }}>
        {t("page.subtitle")}
      </p>

      {offers.isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <CSpinner color="primary" />
        </div>
      ) : partners.length === 0 ? (
        // Also what a customer sees before any partner has a code configured, which is the state
        // this ships in — so it says something true rather than looking broken.
        <CAlert color="info" className="mb-0">
          {t("page.empty")}
        </CAlert>
      ) : (
        <CRow xs={{ gutter: 4 }}>
          {partners.map((offer) => (
            <CCol md={6} key={offer.partnerKey}>
              <PartnerOfferState
                offer={offer}
                busy={record.isPending}
                statusLabel={statusLabel(offer.status, t)}
                statusColour={STATUS_COLOUR[offer.status]}
                onDecision={(decision) => handleDecision(offer.partnerKey, decision)}
              />
            </CCol>
          ))}
        </CRow>
      )}
    </div>
  );
}

function statusLabel(
  status: PartnerOfferStatus,
  t: (key: string, options?: Record<string, unknown>) => string
): string | undefined {
  const key = STATUS_KEY[status];
  return key ? t(`page.status.${key}`) : undefined;
}

interface PartnerOfferStateProps {
  offer: PartnerOfferDto;
  busy: boolean;
  statusLabel?: string;
  statusColour?: string;
  onDecision: (decision: PartnerOfferDecision) => void;
}

/**
 * The card plus what the customer has already said about it. Everything is listed here whatever
 * their answer — a settled partner shows its standing and a way back rather than disappearing.
 */
function PartnerOfferState({
  offer,
  busy,
  statusLabel,
  statusColour,
  onDecision,
}: PartnerOfferStateProps) {
  // Only when the dashboard has actually stopped asking. Reopen stores itself as a snooze with no
  // end date -- the encoding that lets it escape the snooze cap -- so reading `status` here would
  // have a just-reopened offer badge itself "Hidden for now", which is the opposite of what the
  // customer asked for. Visibility is what they will actually experience.
  const settled = offer.visibility === "Hidden";

  // The badge says where they stand and nothing more. There is deliberately no "show me this
  // again": the card and its call to action are on this page whatever they answered, so the button
  // only ever put the offer back in the dashboard slot -- our benefit, not theirs. Undo lives in
  // the toast at the moment of the click instead.
  const status = settled && statusLabel ? (
    <div className="mb-3">
      <CBadge color={statusColour ?? "secondary"}>{statusLabel}</CBadge>
    </div>
  ) : undefined;

  return (
    <PartnerOfferCard
      offer={offer}
      onDecision={onDecision}
      busy={busy}
      // h-100 so a row of partners lines up regardless of how long each one's copy runs; without
      // it the card with an offer line stands taller and the row reads as unbalanced.
      className="h-100"
      // Inside the card rather than floating above it, so each column is one visual block.
      statusSlot={status}
      // The dismissals belong to the prompt. Here the status badge and its undo say the same
      // thing more directly, and a "Not now" on a page you navigated to makes no sense.
      showDismissals={false}
    />
  );
}

export default PartnerOffers;
